<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors }">
        <responsive-button
            v-if="showButton"
            variant="primary"
            @click="$bvModal.show(modalId)"
            :icon="['fas', 'bookmark']"
            :responsiveness="responsiveness"
            class="w-100"
            :disabled="disabled"
        ></responsive-button>
        <modal
            @show="onShow"
            :id="modalId"
            title="Bookmarked Report Settings"
            @ok.prevent="submit"
            :ok-disabled="!meta.valid"
            @shown="setData"
        >
            <b-alert v-if="!isReportSettingsPage" show variant="info">
                Bookmarked reports (with settings below) are made available to the triggers system where they can be
                scheduled to run and sent to various team members. The parameters below ONLY apply to bookmarked
                reports. A list of all bookmarked reports (and their settings) are viewable
                <a class="link text-decoration-underline" href="/app/reports/report-settings"><b>here</b></a
                >.
            </b-alert>
            <validation-provider name="name" rules="required">
                <form-input
                    v-model="values.name"
                    label="Name"
                    :error="errors.name"
                    @input="(val) => setFieldValue('name', val)"
                />
            </validation-provider>
            <validation-provider name="type" rules="required" v-if="values.report_sysname !== 'OWNER_STATEMENTS'">
                <form-select
                    :options="reportTypeOptions"
                    v-model="values.type"
                    label="Report Type"
                    :error="errors.type"
                    @change="(val) => setFieldValue('type', val)"
                />
            </validation-provider>

            <div class="w-100 d-flex justify-content-between flex-wrap">
                <validation-provider
                    v-for="item in values.settings"
                    :key="item.name"
                    :rules="item.required ? 'required' : ''"
                    :name="`settings.${item.name}`"
                >
                    <div class="w-100 d-block">
                        <form-input
                            v-if="item.type === TEXT_INPUT"
                            v-model="item.value"
                            floatingLabel="Name"
                            :label="item.label"
                            :error="errors[`settings.${item.name}`]"
                            @input="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                        <span v-if="item.type === LABEL">
                            {{ item.label }}:
                            {{ capitalize(startCase(item.value || '')) }}
                        </span>
                        <form-select
                            v-if="item.type === DATE_PERIODS_INPUT"
                            :options="getDatePeriodsOptions(item)"
                            v-model="item.value"
                            :label="item.label"
                            :error="errors[`settings.${item.name}`]"
                            @change="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                        <form-select
                            v-if="item.type === SELECT_INPUT"
                            :options="item.options"
                            v-model="item.value"
                            :label="item.label"
                            :error="errors[`settings.${item.name}`]"
                            @change="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                        <form-search-multiselect
                            v-if="item.type === MULTI_SELECT_INPUT"
                            :options="getMultiselectOptions(item)"
                            v-model="item.value"
                            :label="item.label"
                            :error="errors[`settings.${item.name}`]"
                            :placeholder="item.placeholder || 'All Items'"
                            :hideSelectAll="item.label.toLowerCase().indexOf('properties') !== -1"
                            :selectSize="2"
                            :responsiveness="true"
                            class="properties-multiselect"
                            @input="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                        <form-square-switch
                            v-if="item.type === SWITCH_INPUT"
                            v-model="item.value"
                            :labels="{
                                checked: item.label,
                                unchecked: item.label,
                            }"
                            :sync="true"
                            class="mb-2"
                            :error="errors[`settings.${item.name}`]"
                            @input="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                        <properties-search-multiselect
                            v-if="item.type === PROPERTIES_MULTI_SELECT"
                            v-model="item.value"
                            variant="default"
                            :label="item.label"
                            :properties="properties"
                            :isForm="true"
                            :error="errors[`settings.${item.name}`]"
                            :placeholder="item.placeholder"
                            :controlValue="item.placeholder"
                            :hideSelectAll="true"
                            :selectSize="2"
                            :responsiveness="true"
                            class="properties-multiselect"
                            @input="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                        <form-datepicker
                            v-if="item.type === DATE_PICKER_INPUT"
                            v-model="item.value"
                            :label="item.label"
                            :hide-header="true"
                            :display-format="userUnicodeDateFormat"
                            :error="errors[`settings.${item.name}`]"
                            @input="(val) => setFieldValue(`settings.${item.name}`, val)"
                        />
                    </div>
                </validation-provider>
            </div>
        </modal>
    </validation-observer>
</template>

<script>
    import { mapGetters } from 'vuex';
    import capitalize from 'lodash/capitalize';
    import isArray from 'lodash/isArray';
    import isObject from 'lodash/isObject';
    import startCase from 'lodash/startCase';

    import api from '../../../api';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSquareSwitch from '../../generic/form-square-switch/form-square-switch.vue';
    import PropertiesSearchMultiselect from '../../generic/properties-search-multiselect/properties-search-multiselect.vue';
    import FormSearchMultiselect from '../../generic/form-search-multiselect/form-search-multiselect.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
    import {
        YESTERDAY,
        TODAY,
        TOMORROW,
        LAST_WEEK,
        THIS_WEEK,
        NEXT_WEEK,
        LAST_MONTH,
        THIS_MONTH,
        NEXT_MONTH,
        THIS_YEAR,
        LAST_YEAR,
        CUSTOM_RANGE,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import { formatResponseError } from '../../../utils/api';
    import { REPORT_ARRIVALS_PROPERTIES_ENDPOINT, REPORT_SETTINGS_ENDPOINT } from '../../../endpoints';
    import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
    import truncate from 'lodash/truncate';

    export const MULTI_SELECT_INPUT = 'MULTI_SELECT_INPUT';
    export const SELECT_INPUT = 'SELECT_INPUT';
    export const SWITCH_INPUT = 'SWITCH_INPUT';
    export const TEXT_INPUT = 'TEXT_INPUT';
    export const DATE_PERIODS_INPUT = 'DATE_PERIODS_INPUT';
    export const PROPERTIES_MULTI_SELECT = 'PROPERTIES_MULTI_SELECT';
    export const DATE_PICKER_INPUT = 'DATE_PICKER_INPUT';
    export const LABEL = 'STRING';

    export default {
        components: {
            Modal,
            FormInput,
            FormSelect,
            FormSearchMultiselect,
            FormSquareSwitch,
            PropertiesSearchMultiselect,
            FormDatepicker,
            ResponsiveButton,
        },
        props: {
            initialValues: {
                type: Object,
                default: () => {
                    return {
                        name: '',
                        report_sysname: '',
                        settings: [],
                        type: '',
                    };
                },
            },
            modalId: {
                type: String,
                default: '',
            },
            excelOnly: {
                type: Boolean,
                default: false,
            },
            responsiveness: {
                type: Boolean,
                default: true,
            },
            disabled: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                    LAST_YEAR,
                    CUSTOM_RANGE,
                ],
                DATE_PERIODS_INPUT,
                SELECT_INPUT,
                MULTI_SELECT_INPUT,
                SWITCH_INPUT,
                TEXT_INPUT,
                PROPERTIES_MULTI_SELECT,
                DATE_PICKER_INPUT,
                LABEL,
                values: {
                    name: '',
                    report_sysname: '',
                    settings: [],
                    type: '',
                },
                properties: [],
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat', 'userPermissions']),
            isReportSettingsPage() {
                return this.$route.path.indexOf('report-settings') > -1;
            },
            showButton() {
                return (
                    !this.isReportSettingsPage &&
                    this.userPermissions &&
                    this.userPermissions.can_manage_report_settings
                );
            },
            reportTypeOptions() {
                const options = [
                    {
                        text: 'Excel',
                        value: 'EXCEL',
                    },
                ];
                if (!this.excelOnly) {
                    options.push({
                        text: 'PDF',
                        value: 'PDF',
                    });
                }
                return options;
            },
            hasPropertiesSelect() {
                return this.values.settings.some(
                    (item) =>
                        item.type === PROPERTIES_MULTI_SELECT ||
                        (item.type === MULTI_SELECT_INPUT && item.name === 'properties')
                );
            },
            propertiesOptions() {
                return this.properties.map((item) => ({
                    value: item.property_id.toString(),
                    text: item.name,
                }));
            },
        },
        methods: {
            startCase,
            capitalize,
            setData() {
                this.$refs.form.setValues({
                    name: this.values.name,
                    type: this.values.type,
                });
                this.values.settings.forEach((setting) => {
                    this.$refs.form.setFieldValue(`settings.${setting.name}`, setting.value);
                });
            },
            onShow() {
                this.values = {
                    ...this.initialValues,
                    type: this.initialValues.type ? this.initialValues.type : this.excelOnly ? 'EXCEL' : 'PDF',
                };
                if (this.hasPropertiesSelect) {
                    this.fetchProperties();
                }
            },
            getMultiselectOptions(item) {
                if (item.name === 'properties') {
                    return this.propertiesOptions;
                }
                return item.options;
            },
            getDatePeriodsOptions(item) {
                if (item.predefinedRanges) {
                    return item.predefinedRanges.map((item) => ({
                        value: item.split(' ').join('_'),
                        text: capitalize(item),
                    }));
                }
            },
            async fetchProperties() {
                try {
                    const { data } = await api.get(REPORT_ARRIVALS_PROPERTIES_ENDPOINT());
                    this.properties = data.map((item) => ({ ...item, name: truncate(item.name) }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch arrivals' });
                }
            },
            async submit() {
                try {
                    const settings = this.values.settings.map((item) => {
                        if (isArray(item.value)) {
                            const idList = item.value
                                .map((id) => {
                                    if (isObject(id)) {
                                        return id.property_id;
                                    }
                                    return id;
                                })
                                .filter((idListItem) => !!idListItem);
                            return {
                                ...item,
                                value: idList.join(','),
                            };
                        }
                        return item;
                    });
                    const body = {
                        ...this.values,
                        settings: JSON.stringify(settings),
                    };
                    if (body.report_sysname === 'OWNER_STATEMENTS') {
                        body.type = this.values.settings
                            .find((item) => item.name === 'report_format')
                            ?.value.toUpperCase();
                    }

                    if (this.values.id) {
                        await api.patch(REPORT_SETTINGS_ENDPOINT(body.id), body);
                    } else {
                        await api.post(REPORT_SETTINGS_ENDPOINT(), body);
                    }
                    this.successToast({ text: 'Settings were successfully saved' });
                    this.$bvModal.hide(this.modalId);
                    this.$emit('submit', this.values);
                } catch (e) {
                    if (isObject(e.response.data) && '__all__' in e.response.data) {
                        this.errorToast({
                            text: e.response.data.__all__[0],
                        });
                    } else {
                        const { generic } = formatResponseError(e);
                        this.errorToast({
                            text: generic || 'Could not save report settings.',
                        });
                    }
                }
            },
        },
    };
</script>

<style lang="scss" scoped>
    :deep(.properties-multiselect) {
        .form-search-multiselect__options-group,
        .form-search-multiselect__selected-options-group {
            max-height: 150px !important;
        }
    }
</style>
